import { Button } from "@dropbox/dig-components/buttons";
import { parseToolCall } from "@mirage/mosaics/Chat/components/chat/ConversationMessages/ToolCalls/parsers";
import { ToolCallRow } from "@mirage/mosaics/Chat/components/chat/ConversationMessages/ToolCalls/ToolCallRow";
import type { ToolCallsRunner } from "@mirage/mosaics/Chat/components/chat/ConversationMessages/ToolCalls/ToolCallsRunner";
import type { ParsedToolCall } from "@mirage/mosaics/Chat/components/chat/ConversationMessages/ToolCalls/types";
import type { AgentId, ToolCall } from "@mirage/service-conversation/types";
import i18n from "@mirage/translations";
import { memo, useMemo } from "react";

interface ConversationMessageToolCallsProps {
  toolCalls: ParsedToolCall[];
  toolCallsRunner: ToolCallsRunner;
  showConfirmation: boolean;
}
export const ConversationMessageToolCalls = memo(
  ({
    toolCalls,
    toolCallsRunner,
    showConfirmation,
  }: ConversationMessageToolCallsProps) => {
    const hasStartedOrRanToolCalls = Object.values(
      toolCallsRunner.toolCalls,
    ).some(({ status }) => status !== undefined);
    return (
      <div>
        {toolCalls.map((toolCall) => (
          <ToolCallRow
            key={toolCall.id}
            toolCall={toolCall}
            status={toolCallsRunner.toolCalls[toolCall.id]?.status}
          />
        ))}
        {showConfirmation && toolCalls.length > 0 && (
          <Button
            size="small"
            variant="primary"
            onClick={toolCallsRunner.runToolCalls}
            disabled={hasStartedOrRanToolCalls}
          >
            {i18n.t("confirm_tool_calls")}
          </Button>
        )}
      </div>
    );
  },
);
ConversationMessageToolCalls.displayName = "ConversationMessageToolCalls";

/**
 * Parse and return all tool calls that are recognizable (e.g. ignore server-side tools).
 */
export function useParsedToolCalls(
  toolCalls: ToolCall[] | undefined,
  agentId: AgentId,
): ParsedToolCall[] {
  return useMemo(() => {
    if (!toolCalls) {
      return [];
    }
    const parsedToolCalls: ParsedToolCall[] = [];
    for (const toolCall of toolCalls) {
      const parsedToolCall = parseToolCall(toolCall, agentId);
      if (parsedToolCall) {
        parsedToolCalls.push(parsedToolCall);
      }
    }
    return parsedToolCalls;
  }, [toolCalls, agentId]);
}
